import { Nav, NavItem, NavLink, Card, CardHeader, CardBody, TabContent, TabPane } from 'reactstrap';
import { useContext, useState, useEffect } from 'react';
import EmployeeSettingsForm, { settingsType } from './EmployeeSettingsForm';
import PtoPolices from './PtoPolices';
import LoaPolices from './LoaPolices';
import HireAgreementsForm from './HireAgreementsForm';
import PolicyViolationsForm from './PolicyViolationsForm';
import BulkUpdate from './BulkUpdate';
import { Context as AuthContext } from '../../../contexts/AuthContext';
import isHRUser from '../../../helpers/isHRUser';
import { ucfirst } from '../../../helpers/string';
import useCheckMobileScreen from '../../../hooks/useCheckMobileScreen';

type EmployeeSettingsProps = {
  isOpen: boolean;
};

const customStyles = {
  navLink: {
    minHeight: '42px',
    paddingTop: '8px',
    cursor: 'pointer',
  },
};

const EmployeeSettingsTabs = ({ isOpen }: EmployeeSettingsProps) => {
  const { isMobile, isTablet } = useCheckMobileScreen();
  const { user }: any = useContext(AuthContext);
  const [activeTab, setActiveTab] = useState(isHRUser(user) ? 'ptoPolicies' : 'titles');
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const isMediumScreen = windowWidth <= 834 && windowWidth > 768;
  const isSmallScreen = windowWidth <= 768 || isMobile || isTablet;
  const settingsTabs = {
    titles: {
      content: <EmployeeSettingsForm isOpen={isOpen} type={settingsType.TITLE} />,
      name: 'Titles',
      icon: 'fas fa-user-tie',
      visible: true,
    },
    departments: {
      content: <EmployeeSettingsForm isOpen={isOpen} type={settingsType.DEPARTMENT} />,
      name: 'Departments',
      icon: 'fas fa-building',
      visible: true,
    },
    ptoPolicies: {
      content: <PtoPolices isOpen={isOpen} />,
      name: 'PTO Policies',
      icon: 'fas fa-business-time',
      visible: true,
    },
    hireAgreements: {
      content: <HireAgreementsForm isOpen={isOpen} />,
      name: 'Hire Agreements',
      icon: 'fas fa-file',
      visible: true,
    },
    policyViolations: {
      content: <PolicyViolationsForm isOpen={isOpen} />,
      name: 'Policy Violations',
      icon: 'fas fa-exclamation-circle',
      visible: true,
    },
    loaPolicies: {
      content: <LoaPolices isOpen={isOpen} />,
      name: 'LOA Policies',
      icon: 'fas fa-business-time',
      visible: true,
    },
    bulkUpdate: {
      content: <BulkUpdate isOpen={isOpen} />,
      name: 'Bulk Update',
      icon: 'fas fa-edit',
      visible: true,
    },
  };

  const isVisible = (tabKey: string) => {
    return (settingsTabs as any)[tabKey]?.visible;
  };

  const getTabName = (tabKey: string) => {
    return (settingsTabs as any)[tabKey]?.name || ucfirst(tabKey);
  };

  return (
    <>
      <Card className="bg-secondary shadow">
        <CardHeader
          className="bg-white border-0"
          style={
            isSmallScreen
              ? { paddingBottom: '10px' }
              : isMediumScreen
              ? { paddingBottom: '20px', maxHeight: 'none' }
              : { maxHeight: '62px' }
          }
        >
          <div
            style={{
              overflowX: 'auto',
              overflowY: 'hidden',
              WebkitOverflowScrolling: 'touch',
              whiteSpace: 'nowrap',
              marginBottom: isSmallScreen ? '5px' : isMediumScreen ? '15px' : '0',
            }}
          >
            <Nav
              tabs
              className="nav-profile"
              style={{
                flexWrap: 'nowrap',
                display: 'flex',
              }}
            >
              {Object.keys(settingsTabs).map((tab: string, i: number) => (
                <>
                  {isVisible(tab) && (
                    <NavItem
                      className="cursor-pointer"
                      key={`nav-item_${i}`}
                      style={{
                        flexShrink: 0,
                        minWidth: 'fit-content',
                      }}
                    >
                      <NavLink
                        className="border-0 cursor-pointer font-weight-bold nav-link-profile"
                        style={customStyles.navLink}
                        active={activeTab === tab}
                        onClick={() => setActiveTab(tab)}
                      >
                        <i className={`${(settingsTabs as any)[tab]?.icon} ${!isSmallScreen ? 'mr-2' : ''}`} />
                        <span>{getTabName(tab)}</span>
                      </NavLink>
                    </NavItem>
                  )}
                </>
              ))}
            </Nav>
          </div>
        </CardHeader>
        <CardBody
          className="pt-0"
          style={{
            marginTop: isSmallScreen ? '0.5rem' : isMediumScreen ? '1rem' : '0.75rem',
          }}
        >
          <TabContent activeTab={activeTab}>
            {Object.keys(settingsTabs).map((tab: string, i: number) => (
              <TabPane key={`tab-pane_${i}`} tabId={tab}>
                {isVisible(tab) && activeTab === tab && (settingsTabs as any)[tab].content}
              </TabPane>
            ))}
          </TabContent>
        </CardBody>
      </Card>
    </>
  );
};

export default EmployeeSettingsTabs;
